import { FeatureFlag, getFeatureFlag } from 'constants/feature-flag';
import { useAuth } from 'contexts/auth';
import { useCart } from 'contexts/cart';
import { useCustomerDetails } from 'hooks/customer/use-customer';
import Search from 'modules/header/search';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import IconCart from 'src/assets/icons/cart.svg';
import IconUser from 'src/assets/icons/user-minimal.svg';
import { gtm, selectElement } from 'tracking';
import getShopId from 'utils/i18n/get-shop-id';
import { useDom } from '../../../../contexts/dom';
import FloatingIndicator from '../../floating-indicator';
import MobileTest from '../mobile/mobile-test';

const PetProfileNavbarLink = dynamic(
  () => import('components/pet-profile/navbar-link'),
  {
    ssr: false,
  }
);

interface PropTypes {
  isExperimentLoading: boolean;
  variant?: number;
}

const CustomerLinks: React.FC<PropTypes> = ({
  isExperimentLoading,
  variant,
}) => {
  const intl = useIntl();
  const { headerCartIconRef, headerAccountIconRef } = useDom();
  const { isUserLoggedIn, customer } = useAuth();
  const customerId = isUserLoggedIn ? customer?.customerId : undefined;
  const { details } = useCustomerDetails(customerId);
  const firstName = details?.firstName;
  const { pathname } = useRouter();
  const { itemsCount } = useCart();
  const { locale } = useRouter();
  const shopId = getShopId(locale);
  const featureFlag = getFeatureFlag(FeatureFlag.petProfile, shopId);

  const trackingFunction = (element: string): void => {
    gtm(
      selectElement({
        element: ['Header', element],
      })
    );
  };

  /**
   * Handles clicks on the account icon/link.
   * Prevents default navigation and layout shift when already on the login page.
   */
  const handleAccountClick = (e: React.MouseEvent): void => {
    if (pathname === '/login') {
      e.preventDefault();
      return;
    }
    trackingFunction('Account');
  };

  return (
    <>
      <div className="flex items-center gap-2">
        <div className="z-search relative h-[36px] lg:hidden">
          <Search />
        </div>
        {featureFlag && <PetProfileNavbarLink />}

        <Link
          href="/account"
          ref={headerAccountIconRef}
          data-test="nav-account"
          title={intl.formatMessage({ id: 'header:account' })}
          className="text-grey-700/60 hover:text-grey-700/80 relative flex h-9 flex-col items-center justify-center leading-none"
          onClick={handleAccountClick}
        >
          <IconUser className="text-primary-main lg:text-text-dark-bg-contrast-white w-6 shrink-0 lg:w-7" />
          <span className="text-xxs text-primary-main lg:text-text-dark-bg-contrast-white leading-4 font-medium">
            {isUserLoggedIn && firstName && (
              <FormattedMessage
                id="header:account:greeting"
                values={{ firstName: firstName }}
              />
            )}
            {!isUserLoggedIn && <FormattedMessage id="header:account" />}
          </span>
        </Link>

        <Link
          href="/cart"
          ref={headerCartIconRef}
          data-test="nav-cart"
          title={intl.formatMessage({ id: 'header:cart' })}
          className="text-grey-700/60 hover:text-grey-700/80 relative flex h-9 flex-col items-center justify-center leading-none"
          onClick={() => trackingFunction('Cart')}
        >
          <IconCart className="text-primary-main lg:text-text-dark-bg-contrast-white w-6 shrink-0 lg:w-7" />
          <FloatingIndicator count={itemsCount} />
          <span className="text-xxs text-primary-main lg:text-text-dark-bg-contrast-white leading-4 font-medium">
            <FormattedMessage id="header:cart" />
          </span>
        </Link>
        {variant === 1 && !isExperimentLoading && <MobileTest />}
      </div>
    </>
  );
};
export default CustomerLinks;
